import React from "react";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-slate-800 mb-2">{title}</h2>
    <div className="text-sm text-slate-600 leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10 border-b border-slate-200 pb-6">
          <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
          <p className="text-sm text-slate-500 mt-2">PM Home Remodeling — Labor Bill Management App</p>
          <p className="text-xs text-slate-400 mt-1">Effective date: June 1, 2026 · Last updated: June 2026</p>
        </header>

        <Section title="1. Overview">
          <p>
            PM Home Remodeling ("we", "us") operates the PM Home Remodeling mobile and web application
            (the "App"). The App is an internal business tool used by our project managers, supervisors,
            and administrative staff to manage remodeling labor bills, crew schedules, logistics, and
            related reporting. This policy explains what information the App collects, how it is used,
            and the choices you have.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>The App collects only the information needed to operate our internal business workflows:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account information:</strong> username, role (e.g., PM, supervisor, admin), and login credentials (passwords are stored securely in hashed form).</li>
            <li><strong>Work data:</strong> labor bills, crew names, job site addresses, schedules, and related business documents entered by authorized users.</li>
            <li><strong>Photos and files:</strong> images or documents you choose to attach to jobs or bills, using the camera or photo library with your permission.</li>
            <li><strong>Device push token:</strong> a notification identifier used solely to deliver work-related push notifications to your device.</li>
            <li><strong>Basic usage logs:</strong> access times and actions inside the App, used for security auditing and troubleshooting.</li>
          </ul>
          <p>We do not collect advertising identifiers, contacts, precise background location, or any data unrelated to work.</p>
        </Section>

        <Section title="3. How We Use Information">
          <ul className="list-disc pl-5 space-y-1">
            <li>To authenticate users and control access to business data.</li>
            <li>To create, manage, and share labor bills and crew schedules.</li>
            <li>To display job locations and calculate routes on maps.</li>
            <li>To send work-related notifications (e.g., bill approvals, schedule changes).</li>
            <li>To maintain security, prevent unauthorized access, and troubleshoot issues.</li>
          </ul>
        </Section>

        <Section title="4. Third-Party Services">
          <p>The App relies on a small number of service providers, strictly to support the features above:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Google Firebase Cloud Messaging</strong> — delivery of push notifications.</li>
            <li><strong>Google Maps Platform</strong> — displaying maps, geocoding job addresses, and route measurement.</li>
            <li><strong>Email delivery service</strong> — sending business emails (e.g., bills to the finance team).</li>
          </ul>
          <p>These providers process data only as needed to provide their service and are bound by their own privacy policies.</p>
        </Section>

        <Section title="5. Data Sharing and Selling">
          <p>
            We do <strong>not</strong> sell, rent, or trade any personal information. Data is shared only
            internally among authorized company staff, or with the service providers listed above, or when
            required by law.
          </p>
        </Section>

        <Section title="6. Data Retention and Security">
          <p>
            Business records are retained for as long as needed for accounting, legal, and operational
            purposes. Access to the App is restricted to authorized users, protected by authentication,
            and all traffic is encrypted in transit (HTTPS). You may request deletion of your account data
            at any time using the contact below.
          </p>
        </Section>

        <Section title="7. Permissions Used by the Mobile App">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Camera / Photo library:</strong> only when you attach photos to jobs or bills.</li>
            <li><strong>Notifications:</strong> only to deliver work-related alerts; you can disable them in your device settings.</li>
          </ul>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            The App is a business tool intended exclusively for adult employees and contractors. It is not
            directed at children under 13, and we do not knowingly collect information from children.
          </p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>
            We may update this policy from time to time. The "Last updated" date at the top reflects the
            most recent revision. Continued use of the App after changes means you accept the updated policy.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            If you have questions about this policy or want to request access or deletion of your data,
            contact us at:
          </p>
          <p className="font-medium text-slate-800" data-testid="privacy-contact-email">
            📧 labor@pm-home-remodeling.com
          </p>
        </Section>

        <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-400">
          © {new Date().getFullYear()} PM Home Remodeling. All rights reserved.
        </footer>
      </div>
    </div>
  );
}
